import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { FUENTE, useTema } from './tema';
import { fraccionDelClic, recuadroEn, encerrar, escalasDeLaHoja } from './geometriaDeFirmas';

/**
 * VisorDePdf — ver el documento y señalar sobre él dónde van las firmas.
 *
 * Lo usan las dos puntas del proceso, y por eso vive aparte:
 *
 *   · Quien MANDA a firmar abre el PDF y marca con un clic en qué hoja y en qué
 *     punto firma cada persona (`alColocar`).
 *   · Quien FIRMA abre el mismo PDF, navega por él, ve su recuadro y los de los
 *     demás —firmados y pendientes— y firma en el suyo.
 *
 * Es la misma pantalla con una diferencia de permiso, así que es el mismo
 * componente con una propiedad de más.
 *
 * ── Por qué las coordenadas son fracciones y nunca píxeles ───────────────
 *
 * Lo que sale de aquí es `{pagina, x, y, ancho, alto}` con todo entre 0 y 1,
 * relativo al tamaño de SU página y con el origen arriba a la izquierda.
 *
 * Es deliberado y es lo que sostiene el resto: el PDF se dibuja al ancho que
 * haya —una ventana estrecha, un móvil, la mitad de un portátil— y un píxel
 * aquí no significa nada allí. Guardando fracciones, el 0,5/0,5 es el centro de
 * la hoja en cualquier pantalla y también en el PDF final.
 *
 * La conversión a puntos se hace en un solo sitio, al estampar, y está en
 * `azure-functions/v2/firmas/FirmaEnPdf.js`. Ojo con el origen: ahí abajo el
 * PDF mide desde la esquina INFERIOR, y la resta está hecha. Aquí arriba todo
 * mide desde ARRIBA, como el navegador.
 *
 * ── Por qué pdf.js y no un <iframe> ─────────────────────────────────────
 *
 * Porque un visor nativo no deja poner nada encima ni saber dónde se hizo clic:
 * el PDF del navegador es una caja negra. Para señalar un punto hace falta
 * dibujar las páginas una a una, y eso es lo que pdf.js hace.
 *
 * El worker se carga de `/pdf.worker.min.mjs`, que copia
 * `deployment-scripts/copiar-worker-pdf.js` en cada arranque; el porqué de la
 * copia está allí. En producción hace falta `worker-src` en la CSP de
 * `staticwebapp.config.json`, o esto no arranca SOLO en producción.
 */

const estilos = (C) => ({
  caja: { fontFamily: FUENTE, display: 'flex', flexDirection: 'column', gap: 10 },

  barra: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    padding: '8px 10px', background: C.panelAlto, border: `1px solid ${C.borde}`,
    borderRadius: 10
  },

  boton: {
    padding: '5px 10px', background: C.panel, color: C.texto,
    border: `1px solid ${C.borde}`, borderRadius: 7, cursor: 'pointer',
    fontSize: 13, fontFamily: FUENTE
  },

  cuenta: { fontSize: 13, color: C.suave, minWidth: 92, textAlign: 'center' },

  lienzoFuera: (alto) => ({
    // Altura DEFINIDA, no un maximo: «hoja entera» necesita saber contra que
    // encajar, y con `maxHeight` el hueco mide lo que mida el contenido —que
    // es justo lo que se esta intentando calcular—.
    position: 'relative', overflow: 'auto', height: alto,
    background: C.fondoHondo, border: `1px solid ${C.borde}`, borderRadius: 10,
    // `flex-start` para que una hoja mas baja que el hueco no se estire.
    padding: 12, display: 'flex', justifyContent: 'center', alignItems: 'flex-start'
  }),

  // La hoja. `position: relative` porque los recuadros se posicionan contra
  // ella, no contra el contenedor que se desplaza.
  hoja: (colocando) => ({
    position: 'relative', lineHeight: 0,
    boxShadow: '0 1px 6px rgba(0,0,0,.25)',
    cursor: colocando ? 'crosshair' : 'default'
  }),

  // El fondo, BLANCO y fijo, no el del tema. pdf.js dibuja el contenido de la
  // hoja pero no necesariamente su fondo, asi que sobre el tema oscuro una
  // pagina se veria negra con el texto negro encima. Un documento es blanco
  // aunque la aplicacion que lo enseña no lo sea.
  lienzo: { display: 'block', maxWidth: '100%', height: 'auto', background: '#ffffff' },

  // Un recuadro de firma. Todo en porcentajes: ver la cabecera.
  recuadro: (c, mio, firmado) => ({
    position: 'absolute',
    left: `${c.x * 100}%`,
    top: `${c.y * 100}%`,
    width: `${c.ancho * 100}%`,
    height: `${c.alto * 100}%`,
    border: `2px ${firmado ? 'solid' : 'dashed'} ${firmado ? C.exito : (mio ? C.acento : C.tenue)}`,
    background: firmado ? 'transparent' : (mio ? 'rgba(59,130,246,.14)' : 'rgba(100,116,139,.10)'),
    borderRadius: 4,
    boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    // Cuando ya hay una firma estampada en el PDF, el recuadro no debe tapar
    // nada: solo enmarca lo que se ve debajo.
    pointerEvents: firmado ? 'none' : 'auto',
    // Que arrastrar el recuadro no se interprete como desplazar la página. Sin
    // esto, mover una firma desde una tableta o un movil hace scroll y la firma
    // no se mueve. Va en el recuadro y no en la hoja: la hoja SI tiene que
    // poder desplazarse con el dedo.
    touchAction: 'none'
  }),

  etiqueta: (mio) => ({
    fontSize: 10.5, lineHeight: 1.15, padding: '1px 4px', borderRadius: 3,
    background: mio ? C.acento : C.panel,
    color: mio ? C.acentoTexto : C.suave,
    border: mio ? 'none' : `1px solid ${C.borde}`,
    maxWidth: '96%', overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', pointerEvents: 'none'
  }),

  quitar: {
    position: 'absolute', top: -9, right: -9, width: 19, height: 19,
    borderRadius: '50%', border: 'none', background: C.error, color: '#fff',
    fontSize: 12, lineHeight: '19px', cursor: 'pointer', padding: 0,
    fontFamily: FUENTE
  },

  // La esquina por la que se agranda. Abajo a la derecha, como en todas partes.
  asa: {
    position: 'absolute', right: -5, bottom: -5, width: 12, height: 12,
    borderRadius: 3, background: C.acento, border: '2px solid #fff',
    cursor: 'nwse-resize'
  },

  aviso: (color) => ({
    padding: '9px 12px', borderRadius: 8, fontSize: 13,
    background: C.panelAlto, border: `1px solid ${color}`, color: C.texto
  }),

  cargando: { padding: 28, textAlign: 'center', color: C.suave, fontSize: 13.5 }
});

/** Carga pdf.js una sola vez, cuando de verdad hace falta. */
let pdfjs = null;
async function libreria() {
  if (pdfjs) return pdfjs;
  // Import dinámico: `pdfjs-dist` pesa cerca de un megabyte y la mayoría de las
  // pantallas de esta aplicación no abren ningún PDF. Así no lo paga quien no
  // lo usa.
  const mod = await import('pdfjs-dist');
  mod.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  pdfjs = mod;
  return pdfjs;
}

/**
 * Un solo worker para todas las aperturas.
 *
 * Por defecto, pdf.js crea uno por documento. El worker son 1,37 MB que hay que
 * instanciar cada vez, y en esta pantalla se abre el mismo acta varias veces
 * —se cierra la ventana, se vuelve a abrir, se cambia de hoja inicial—.
 *
 * Reusarlo es seguro: pdf.js solo se apropia del worker y lo termina al cerrar
 * el documento cuando lo creó él (`task._worker` solo se asigna en ese caso).
 * Pasándole uno nuestro, cerrar un documento no se lleva por delante el worker.
 */
let trabajador = null;

/**
 * Si pdf.js acabó ejecutándose en el HILO PRINCIPAL.
 *
 * Cuando el worker no se puede cargar —la CSP lo bloquea, el servidor devuelve
 * el `.mjs` con otro tipo, el navegador no admite workers de módulo— pdf.js NO
 * falla: cae a `_setupFakeWorker` y hace todo el trabajo en el hilo principal.
 * Funciona, pero cada hoja tarda segundos y la pantalla se congela mientras.
 *
 * Es el peor modo de fallo posible, porque parece que «va lento» y no que algo
 * esté mal configurado, así que se detecta y se dice. `_webWorker` queda en
 * null en ese caso; es campo interno, y por eso solo se usa para avisar y nunca
 * para decidir nada.
 */
let enHiloPrincipal = false;

async function trabajadorCompartido(lib) {
  if (trabajador && !trabajador.destroyed) return trabajador;
  trabajador = new lib.PDFWorker({ name: 'minutas-visor' });
  try {
    await trabajador.promise;
    enHiloPrincipal = !trabajador._webWorker;
  } catch {
    // Que no se pueda ni preparar el worker no es razón para no enseñar el
    // documento: se devuelve null y pdf.js se las arregla como sabe.
    trabajador = null;
    enHiloPrincipal = true;
  }
  return trabajador;
}

/**
 * Dibuja la PRIMERA hoja de un PDF en un lienzo, para una miniatura.
 *
 * Reutiliza la libreria y el worker compartido del visor: una miniatura no
 * merece instanciar otro worker de 1,37 MB, y asi la conversion que ya se hizo
 * sirve para las dos cosas.
 *
 * Los bytes se COPIAN, por lo mismo que en el visor: pdf.js transfiere el
 * ArrayBuffer al worker y deja el original vacio. Sin la copia, pintar la
 * miniatura dejaria inservibles los bytes que luego usan el visor y el envio.
 *
 * @param {Uint8Array} bytes
 * @param {HTMLCanvasElement} lienzo
 * @param {number} anchoCss - a que ancho se va a VER
 * @returns {Promise<void>}
 */
export async function dibujarPrimeraHoja(bytes, lienzo, anchoCss) {
  if (!bytes || !lienzo) return;
  const lib = await libreria();
  const obrero = await trabajadorCompartido(lib);
  const tarea = lib.getDocument({
    data: new Uint8Array(bytes),
    ...(obrero ? { worker: obrero } : {})
  });
  const doc = await tarea.promise;
  try {
    const hoja = await doc.getPage(1);
    const natural = hoja.getViewport({ scale: 1 });
    const escalas = escalasDeLaHoja({
      anchoDisponible: anchoCss,
      anchoNatural: natural.width,
      densidad: window.devicePixelRatio || 1
    });
    const vista = hoja.getViewport({ scale: escalas.pintado });
    lienzo.width = Math.floor(vista.width);
    lienzo.height = Math.floor(vista.height);
    lienzo.style.width = `${Math.floor(natural.width * escalas.css)}px`;
    lienzo.style.height = `${Math.floor(natural.height * escalas.css)}px`;
    const ctx = lienzo.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    await hoja.render({ canvasContext: ctx, viewport: vista }).promise;
  } finally {
    // El worker es compartido y sobrevive; lo que se suelta es el documento.
    doc.destroy();
  }
}

function VisorDePdf({
  /** De dónde se lee el PDF. Una URL, o bytes ya en memoria. */
  url = null,
  datos = null,
  /** Los recuadros a dibujar: `[{pagina, x, y, ancho, alto, nombre, mio, firmada}]`. */
  recuadros = [],
  /** Si se puede colocar. Sin esto el visor es de solo lectura. */
  colocando = false,
  /** `(pagina, {x, y, ancho, alto}) => void` al hacer clic en un hueco. */
  alColocar = null,
  /** `(indice) => void` para quitar uno ya puesto. */
  alQuitar = null,
  /** `(indice, {x, y, ancho, alto}) => void` al arrastrarlo o agrandarlo. */
  alMover = null,
  /** A qué hoja ir al abrir. Para llevar a quien firma directo a la suya. */
  paginaInicial = 1,
  /** Texto bajo la barra, para explicar qué se espera de quien mira. */
  pista = null,
  /** `(totalDeHojas) => void` en cuanto el documento se abre. */
  alAbrir = null,
  /** Lo alto que puede ponerse el marco. En una ventana propia, casi todo. */
  alto = '68vh'
}) {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const lienzo = useRef(null);
  const hoja = useRef(null);
  // El marco que hace scroll. La escala se mide contra ESTE y no contra el div
  // de la hoja: el ancho de la hoja lo da el propio lienzo, asi que medirlo
  // seria circular —y daba siempre el respaldo de 800 px, con el documento
  // pequeño o desbordado segun la ventana—.
  const marco = useRef(null);
  const documento = useRef(null);
  // La tarea de dibujado en curso. pdf.js lanza si se pide dibujar en un lienzo
  // que ya está dibujando, y eso pasa de verdad al pasar hojas rápido.
  const dibujando = useRef(null);

  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(paginaInicial);
  const [cargando, setCargando] = useState(true);
  // Sube cada vez que se abre un documento nuevo.
  //
  // Existe por un fallo concreto: el efecto que dibuja depende de `pintar`, que
  // solo cambia de identidad al cambiar de hoja. Cuando el documento acaba de
  // abrirse ya en la hoja 1, `setPagina(1)` no cambia el estado, el efecto no
  // se vuelve a ejecutar y el lienzo se queda transparente. La barra decía
  // «Hoja 1 de 3» —esa parte sí funcionaba— sobre un rectángulo en blanco.
  const [generacion, setGeneracion] = useState(0);
  // Ver `enHiloPrincipal`: se enseña, porque si no esto solo parece «lento».
  const [sinWorker, setSinWorker] = useState(false);

  // ── Cómo se encaja la hoja en el hueco ───────────────────────────────
  //
  // Por defecto, ENTERA. Es lo que hacía falta y faltaba: al ancho, de una hoja
  // carta solo se ve la mitad de arriba, y una firma va casi siempre abajo. El
  // documento salía bien y aun así no se podía colocar la firma donde se
  // quería, que es un fallo muy incómodo de describir —«no me deja bajar»—.
  const [modo, setModo] = useState('hoja');
  const [zoom, setZoom] = useState(1);
  const [fallo, setFallo] = useState(null);
  const [arrastre, setArrastre] = useState(null);

  // ── Abrir el documento ────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    let abierto = null;

    (async () => {
      setCargando(true);
      setFallo(null);
      try {
        const lib = await libreria();

        // ── Una COPIA de los bytes, siempre ──────────────────────────
        //
        // pdf.js no lee el ArrayBuffer que se le pasa: lo TRANSFIERE al worker,
        // y eso deja el original vacío —«detached»— para siempre. Quien lo
        // llama no tiene por qué saberlo, y normalmente guarda esos bytes en un
        // `useMemo` para no reconvertirlos en cada pintado.
        //
        // El resultado es que la primera apertura funciona y la segunda falla
        // con «An ArrayBuffer is detached and could not be cloned». Y hay
        // segunda apertura casi siempre: React ejecuta los efectos dos veces en
        // desarrollo, y aquí se vuelve a abrir al cambiar de hoja inicial.
        //
        // Copiando aquí, lo que se transfiere es la copia y el original queda
        // intacto. La copia tiene que hacerla EL VISOR y no quien lo llama:
        // copiar un buffer que ya se transfirió una vez no da cero bytes, LANZA
        // («Cannot perform Construct on a detached ArrayBuffer»), y ese mensaje
        // no se parece en nada a la causa.
        let copia = null;
        if (datos) {
          try {
            copia = new Uint8Array(datos);
          } catch {
            throw new Error(
              'Los bytes del documento ya se habían entregado a otro visor y se vaciaron. '
              + 'Vuelve a abrirlo.'
            );
          }
        }

        const obrero = await trabajadorCompartido(lib);
        const tarea = lib.getDocument({
          ...(copia ? { data: copia } : { url }),
          ...(obrero ? { worker: obrero } : {})
        });
        abierto = await tarea.promise;
        if (!vivo) { abierto.destroy(); return; }
        documento.current = abierto;
        setTotal(abierto.numPages);
        // Ya hay documento: que se dibuje, cambie o no el número de hoja.
        setGeneracion(g => g + 1);
        // Ya hay documento: que se dibuje, cambie o no el número de hoja.
        setGeneracion(g => g + 1);
        setPagina(Math.min(Math.max(1, paginaInicial), abierto.numPages));
        // Quien usa el visor necesita saber cuántas hojas hay —para repetir una
        // firma en todas, por ejemplo— y contarlas aquí evita abrir el PDF dos
        // veces solo para eso.
        if (alAbrir) alAbrir(abierto.numPages);
        setSinWorker(enHiloPrincipal);
      } catch (e) {
        if (vivo) {
          setFallo(
            'No se pudo abrir el documento en PDF. '
            + (e && e.message ? e.message : 'Puede que el archivo esté dañado.')
          );
        }
      } finally {
        if (vivo) setCargando(false);
      }
    })();

    return () => {
      vivo = false;
      // Sin esto, cada vez que se abre el visor queda un documento y su worker
      // en memoria. Se nota al tercero o cuarto.
      if (abierto) abierto.destroy();
      documento.current = null;
    };
    // `alAbrir` queda fuera a proposito: si quien llama pasa una funcion nueva
    // en cada pintado —lo normal— incluirla reabriria el documento sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, datos, paginaInicial]);

  // ── Dibujar la hoja que toca ──────────────────────────────────────────
  const pintar = useCallback(async () => {
    const doc = documento.current;
    const tela = lienzo.current;
    if (!doc || !tela) return;

    // Si había un dibujado a medias, se corta: ver `dibujando`.
    if (dibujando.current) {
      try { dibujando.current.cancel(); } catch { /* ya terminó */ }
      dibujando.current = null;
    }

    try {
      const hojaPdf = await doc.getPage(pagina);
      // `- 24` son los 12 px de relleno a cada lado del marco. Sin restarlos,
      // la hoja sale justo esos pixeles mas ancha que el hueco y el marco gana
      // una barra de desplazamiento horizontal permanente.
      const anchoDisponible = Math.max(
        320,
        (marco.current ? marco.current.clientWidth - 24 : 0) || 800
      );
      const natural = hojaPdf.getViewport({ scale: 1 });

      // Dos escalas: a que tamaño se VE la hoja y a cuantos pixeles se
      // DIBUJA. El porque del tope —y lo que costaba no tenerlo— esta en
      // `escalasDeLaHoja`, en geometriaDeFirmas.js, donde se puede probar.
      const altoDisponible = Math.max(
        240,
        (marco.current ? marco.current.clientHeight - 24 : 0) || 600
      );
      const escalas = escalasDeLaHoja({
        anchoDisponible,
        altoDisponible,
        anchoNatural: natural.width,
        altoNatural: natural.height,
        densidad: window.devicePixelRatio || 1,
        modo,
        zoom
      });

      // El viewport ya lleva la escala dentro, así que el lienzo se mide con
      // él y el contexto no necesita transformación. Antes había las dos cosas
      // —viewport escalado Y `setTransform`— y por eso se multiplicaban.
      const vista = hojaPdf.getViewport({ scale: escalas.pintado });
      tela.width = Math.floor(vista.width);
      tela.height = Math.floor(vista.height);
      tela.style.width = `${Math.floor(natural.width * escalas.css)}px`;
      tela.style.height = `${Math.floor(natural.height * escalas.css)}px`;

      const ctx = tela.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      dibujando.current = hojaPdf.render({ canvasContext: ctx, viewport: vista });
      await dibujando.current.promise;
      dibujando.current = null;
    } catch (e) {
      // `RenderingCancelledException` es lo normal al pasar hojas: no es fallo.
      if (e && e.name !== 'RenderingCancelledException') {
        setFallo(`No se pudo dibujar la hoja ${pagina}: ${e.message}`);
      }
    }
    // `generacion` no se usa dentro, pero tiene que estar: es lo que hace que
    // esta funcion cambie de identidad cuando se abre un documento nuevo, y con
    // ella el efecto que dibuja.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, generacion, modo, zoom]);

  useEffect(() => { pintar(); }, [pintar]);

  // Al cambiar el ancho de la ventana hay que volver a dibujar: la escala sale
  // del ancho disponible, y si no, la hoja se queda pequeña o desbordada y los
  // recuadros —que van en porcentajes— dejan de caer sobre lo que enmarcan.
  useEffect(() => {
    let temporizador = null;
    const alCambiar = () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(pintar, 150);
    };
    window.addEventListener('resize', alCambiar);
    return () => { window.removeEventListener('resize', alCambiar); clearTimeout(temporizador); };
  }, [pintar]);

  // ── Colocar, mover y agrandar ─────────────────────────────────────────

  const alPulsarHoja = useCallback((e) => {
    if (!colocando || !alColocar || arrastre) return;
    // Un clic sobre un recuadro que ya está no pone otro encima.
    if (e.target !== hoja.current && e.target !== lienzo.current) return;
    const punto = fraccionDelClic(e, hoja.current);
    if (punto) alColocar(pagina, recuadroEn(punto));
  }, [colocando, alColocar, pagina, arrastre]);

  const empezarArrastre = useCallback((e, indice, modo) => {
    if (!alMover) return;
    e.stopPropagation();
    e.preventDefault();
    const punto = fraccionDelClic(e, hoja.current);
    if (!punto) return;
    const c = recuadros[indice];
    setArrastre({ indice, modo, desde: punto, original: { x: c.x, y: c.y, ancho: c.ancho, alto: c.alto } });
  }, [alMover, recuadros]);

  useEffect(() => {
    if (!arrastre || !alMover) return;

    const mover = (e) => {
      const ahora = fraccionDelClic(e, hoja.current);
      if (!ahora) return;
      const dx = ahora.x - arrastre.desde.x;
      const dy = ahora.y - arrastre.desde.y;
      const o = arrastre.original;
      const nuevo = arrastre.modo === 'agrandar'
        ? { ...o, ancho: o.ancho + dx, alto: o.alto + dy }
        : { ...o, x: o.x + dx, y: o.y + dy };
      alMover(arrastre.indice, encerrar(nuevo));
    };
    const soltar = () => setArrastre(null);

    // En `window` y no en la hoja: quien arrastra se sale del documento a
    // media maniobra, y con los oyentes en la hoja el recuadro se quedaría
    // pegado al puntero para siempre.
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
  }, [arrastre, alMover]);

  // ── Navegar ───────────────────────────────────────────────────────────

  const ir = useCallback((n) => {
    setPagina((p) => Math.min(Math.max(1, n), total || 1));
  }, [total]);

  // Cuántos recuadros hay en cada hoja, para poder decir dónde están sin
  // obligar a pasarlas una por una buscándolos.
  const porHoja = useMemo(() => {
    const cuenta = new Map();
    for (const c of recuadros) cuenta.set(c.pagina, (cuenta.get(c.pagina) || 0) + 1);
    return cuenta;
  }, [recuadros]);

  const deEstaHoja = recuadros
    .map((c, i) => ({ ...c, indice: i }))
    .filter((c) => c.pagina === pagina);

  if (fallo) return <div style={S.aviso(C.error)}>{fallo}</div>;

  return (
    <div style={S.caja}>
      <div style={S.barra}>
        <button type="button" style={S.boton} onClick={() => ir(pagina - 1)} disabled={pagina <= 1}>
          ← Anterior
        </button>
        <span style={S.cuenta}>
          {cargando ? '…' : `Hoja ${pagina} de ${total}`}
        </span>
        <button type="button" style={S.boton} onClick={() => ir(pagina + 1)} disabled={pagina >= total}>
          Siguiente →
        </button>

        {/* ── Cómo se ve la hoja ──────────────────────────────────────
            «Hoja entera» es el que importa y por eso va primero: para poner
            una firma abajo del todo hay que ver el abajo del todo. */}
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6 }}>
          <button
            type="button"
            onClick={() => { setModo('hoja'); setZoom(1); }}
            style={{
              ...S.boton,
              background: modo === 'hoja' ? C.acento : C.panel,
              color: modo === 'hoja' ? C.acentoTexto : C.texto
            }}
            title="Ver la hoja completa, para poder colocar una firma en cualquier parte"
          >
            Hoja entera
          </button>
          <button
            type="button"
            onClick={() => { setModo('ancho'); setZoom(1); }}
            style={{
              ...S.boton,
              background: modo === 'ancho' ? C.acento : C.panel,
              color: modo === 'ancho' ? C.acentoTexto : C.texto
            }}
            title="Llenar el ancho, para leer el texto más grande"
          >
            Al ancho
          </button>
          <button
            type="button"
            style={S.boton}
            onClick={() => setZoom(z => Math.max(0.4, Math.round((z - 0.2) * 10) / 10))}
            title="Alejar"
          >
            −
          </button>
          <button
            type="button"
            style={S.boton}
            onClick={() => setZoom(z => Math.min(3, Math.round((z + 0.2) * 10) / 10))}
            title="Acercar"
          >
            +
          </button>
          {zoom !== 1 && (
            <span style={{ fontSize: 12, color: C.suave, alignSelf: 'center' }}>
              {Math.round(zoom * 100)}%
            </span>
          )}
        </span>

        {/* Las hojas que tienen firmas. Sin esto, en un acta de ocho hojas hay
            que pasarlas todas para saber si falta alguna por colocar. */}
        {porHoja.size > 0 && (
          <span style={{ fontSize: 12.5, color: C.suave, marginLeft: 'auto' }}>
            Firmas en{' '}
            {[...porHoja.keys()].sort((a, b) => a - b).map((n, i) => (
              <button
                key={n}
                type="button"
                onClick={() => ir(n)}
                style={{
                  ...S.boton,
                  padding: '2px 7px', marginLeft: i === 0 ? 4 : 3,
                  background: n === pagina ? C.acento : C.panel,
                  color: n === pagina ? C.acentoTexto : C.texto
                }}
              >
                {n}
              </button>
            ))}
          </span>
        )}
      </div>

      {sinWorker && (
        <div style={S.aviso(C.atencion)}>
          El navegador no pudo cargar el ayudante que dibuja los PDF, así que se está
          dibujando en el hilo principal: cada hoja tarda segundos y la pantalla se
          queda quieta mientras. El documento se ve bien, pero esto hay que arreglarlo
          —suele ser la cabecera <code>worker-src</code> del servidor—.
        </div>
      )}

      {pista && <div style={S.aviso(C.borde)}>{pista}</div>}

      <div ref={marco} style={S.lienzoFuera(alto)}>
        {cargando && <div style={S.cargando}>Abriendo el documento…</div>}

        <div
          ref={hoja}
          style={{ ...S.hoja(colocando), display: cargando ? 'none' : 'block' }}
          onPointerDown={alPulsarHoja}
        >
          <canvas ref={lienzo} style={S.lienzo} />

          {deEstaHoja.map((c) => (
            <div
              // Solo el índice, y esto importa: con la posición dentro de la
              // clave, cada píxel de arrastre la cambiaba, React daba el
              // elemento por otro distinto, lo desmontaba y montaba uno nuevo
              // —y con él se perdía el puntero—. El recuadro se creaba bien y
              // después no había manera de moverlo.
              key={c.indice}
              style={{
                ...S.recuadro(c, c.mio, c.firmada),
                cursor: alMover && !c.firmada ? 'move' : 'default'
              }}
              onPointerDown={(e) => empezarArrastre(e, c.indice, 'mover')}
              title={c.nombre || ''}
            >
              {/* Sobre una firma ya estampada no se escribe nada: el nombre ya
                  va debajo, puesto al estampar. */}
              {!c.firmada && (
                <span style={S.etiqueta(c.mio)}>
                  {c.mio ? 'Tu firma' : (c.nombre || 'Firma')}
                </span>
              )}

              {alQuitar && !c.firmada && (
                <button
                  type="button"
                  style={S.quitar}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); alQuitar(c.indice); }}
                  title="Quitar este recuadro"
                >
                  ×
                </button>
              )}

              {alMover && !c.firmada && (
                <div
                  style={S.asa}
                  onPointerDown={(e) => empezarArrastre(e, c.indice, 'agrandar')}
                  title="Arrastra para cambiar el tamaño"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VisorDePdf;
